import { useCallback } from "react";

import { type VariableValue } from "@doitintl/cmp-models";

import { useAuthContext } from "../../../../Context/AuthContext";
import { CreateVariableModal } from "../../Dialog/CreateVariableModal";
import { type VariableFormSubmitData, type VariableFormValues } from "../../GlobalVariables/types";
import { useGlobalVariables, useLocalVariables } from "../../GlobalVariables/VariableManager";
import { type VariableScope, type VariableType } from "../../types";

type Props = {
  createModalOpen: boolean;
  setCreateModalOpen: (open: boolean) => void;
  initialData: VariableValue | undefined;
  setInitialData: (data: VariableValue | undefined) => void;
  scope: VariableScope;
  flowId?: string;
};

const CreateVariableModalWrapper = ({
  createModalOpen,
  setCreateModalOpen,
  initialData,
  setInitialData,
  scope,
  flowId,
}: Props) => {
  const { createVariable: createGlobalVariable, editVariable: editGlobalVariable } = useGlobalVariables();
  const { createVariable: createLocalVariable, editVariable: editLocalVariable } = useLocalVariables(flowId ?? "");
  const { currentUser } = useAuthContext({ mustHaveUser: true });

  const handleCreateVariable = useCallback(
    async (data: VariableFormValues) => {
      const params = {
        ...data,
        owner: currentUser.email,
      };
      if (scope === "global") {
        await createGlobalVariable(params);
      } else {
        await createLocalVariable(params);
      }
    },
    [createGlobalVariable, currentUser.email, createLocalVariable, scope]
  );

  const handleUpdateVariable = useCallback(
    async (data: VariableFormValues) => {
      if (!initialData) return;

      const params = {
        data,
        id: initialData.id,
      };
      if (scope === "global") {
        await editGlobalVariable(params);
      } else {
        await editLocalVariable(params);
      }
    },
    [editGlobalVariable, editLocalVariable, initialData, scope]
  );

  const handleVariableSubmit = useCallback(
    async (data: VariableFormSubmitData) => {
      const { mode, ...formData } = data;
      if (mode === "create") {
        await handleCreateVariable(formData);
      } else {
        await handleUpdateVariable(formData);
      }
    },
    [handleCreateVariable, handleUpdateVariable]
  );
  return (
    <CreateVariableModal
      open={createModalOpen}
      onClose={() => {
        setCreateModalOpen(false);
      }}
      onSubmit={async (data) => {
        await handleVariableSubmit(data);
        setCreateModalOpen(false);
      }}
      onTransitionExited={() => {
        setInitialData(undefined);
      }}
      scope={scope}
      mode={initialData ? "edit" : "create"}
      initialData={
        initialData
          ? {
              name: initialData.name,
              type: initialData.type as VariableType,
              value: initialData.value,
            }
          : undefined
      }
    />
  );
};

export default CreateVariableModalWrapper;
